// frontend/src/components/ui/DensityToggle.tsx
// Brighella v2 (ADR-013 cross-cutting #6) — Global density toggle.
//
// 3-state segmented control: compact / cozy / comfortable. Persists via
// useCohesionStore (localStorage-backed, per-user). Visible in the user
// menu and on dense surfaces (Calendar, People matrix, Reports) as a
// quick toggle. Doesn't propagate via context — components read density
// from useCohesionStore directly.

// React import not required: jsx: 'react-jsx' transform handles JSX scope.
import { useCohesionStore, type Density } from '../../store/cohesionStore'
import { useLanguage } from '../../i18n/index.jsx'
import './DensityToggle.css'

interface DensityOption {
  value: Density
  label: string
  ariaLabel: string
}

const OPTIONS: DensityOption[] = [
  { value: 'compact',     label: 'Compact',     ariaLabel: 'Compact density (denser rows)' },
  { value: 'cozy',        label: 'Cozy',        ariaLabel: 'Cozy density (default)' },
  { value: 'comfortable', label: 'Comfortable', ariaLabel: 'Comfortable density (more whitespace)' },
]

export interface DensityToggleProps {
  /** Optional className for layout overrides. */
  className?: string
}

export default function DensityToggle({ className }: DensityToggleProps) {
  const { t } = useLanguage()
  const density = useCohesionStore((s) => s.density)
  const setDensity = useCohesionStore((s) => s.setDensity)

  return (
    <div
      className={'density-toggle' + (className ? ' ' + className : '')}
      role="radiogroup"
      aria-label={t('displayDensity')}
    >
      {OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={density === opt.value}
          aria-label={opt.ariaLabel}
          className={
            'density-toggle-option' +
            (density === opt.value ? ' density-toggle-option-active' : '')
          }
          onClick={() => setDensity(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
